<template>
  <div v-if="visible" class="ai-chat-widget">
    <button
      v-if="!isOpen"
      @click="open"
      class="ai-chat-bubble"
      aria-label="Open AI assistant"
    >
      <svg class="ai-chat-sparkle" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 1.5c0 3.5 1.7 6.7 4.5 9-2.8 0-6.5 1.7-9 4.5 0-2.8-1.7-6.5-4.5-9 2.8 0 6.5-1.7 9-4.5z"/>
        <path d="M19.5 14c0 1.6.7 2.9 1.9 3.7-1.2 0-2.7.8-3.9 2-0-1.2-.8-2.7-2-3.7 1.2 0 2.7-.8 4-2z"/>
      </svg>
    </button>

    <div v-else class="ai-chat-panel">
      <div class="ai-chat-header">
        <span class="ai-chat-header-title">
          <svg class="ai-chat-sparkle ai-chat-sparkle-sm" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 1.5c0 3.5 1.7 6.7 4.5 9-2.8 0-6.5 1.7-9 4.5 0-2.8-1.7-6.5-4.5-9 2.8 0 6.5-1.7 9-4.5z"/>
            <path d="M19.5 14c0 1.6.7 2.9 1.9 3.7-1.2 0-2.7.8-3.9 2-0-1.2-.8-2.7-2-3.7 1.2 0 2.7-.8 4-2z"/>
          </svg>
          AI Assistant
        </span>
        <button @click="isOpen = false" aria-label="Close" class="ai-chat-close">×</button>
      </div>

      <div class="ai-chat-messages" ref="messages">
        <div v-if="messages.length === 0" class="ai-chat-empty">
          <p>Ask me anything about this scheme.</p>
          <p class="ai-chat-empty-hint">Try: <em>"water bill history"</em> or <em>"roof leaks"</em></p>
        </div>

        <div
          v-for="(msg, i) in messages"
          :key="i"
          :class="['ai-chat-msg', 'ai-chat-msg-' + msg.role]"
        >
          <div v-if="msg.role === 'user'" class="ai-chat-user-bubble">{{ msg.text }}</div>

          <div v-else-if="msg.role === 'thinking'" class="ai-chat-assistant-bubble ai-chat-thinking">
            <span class="ai-chat-dot"></span>
            <span class="ai-chat-dot"></span>
            <span class="ai-chat-dot"></span>
          </div>

          <div v-else class="ai-chat-assistant-bubble">
            <div class="ai-chat-text">
              <p v-for="(para, j) in msg.paragraphs" :key="j">{{ para }}</p>
            </div>

            <img
              v-if="msg.chart"
              :src="msg.chart"
              :alt="msg.chartAlt"
              class="ai-chat-chart"
            />

            <div v-if="msg.sources" class="ai-chat-sources">
              <div class="ai-chat-sources-label">Sources</div>
              <div class="ai-chat-source-tiles">
                <div v-for="(src, k) in msg.sources" :key="k" class="ai-chat-source-tile">
                  <i class="fa fa-file-text-o"></i>
                  <span>{{ src }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form @submit.prevent="onSubmit" class="ai-chat-input">
        <input
          v-model="inputText"
          type="text"
          placeholder="Ask anything..."
          :disabled="isThinking"
          ref="input"
        />
        <button
          type="submit"
          :disabled="isThinking || !inputText.trim()"
          aria-label="Send"
        >
          <i class="fa fa-paper-plane"></i>
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

const FLAG_KEY = "stratavault.aiDemo";

const SCENARIO_A = {
  paragraphs: [
    "I found 20 water bills for this scheme covering the period April 2021 to April 2026. Of these, 19 were paid and recorded as approved by the committee. One bill from July 2023 appears in the financial records but has no corresponding payment approval on file — you may wish to review this.",
    "The chart below shows quarterly water costs over the five-year period. The overall trend shows a steady increase consistent with water rate adjustments over that period, with no significant anomalies in consumption volume.",
    "I also found 4 plumbing work orders and associated invoices during this period, 2 of which reference water usage issues. These may be relevant to your review.",
    "Is there anything more specific you would like to know?",
  ],
  chart: "/images/ai-chat/poc-water-chart.svg",
  chartAlt: "Quarterly water costs Apr 2021 to Apr 2026",
  sources: [
    "Water Bill Q2 2021.pdf",
    "Water Bill Q3 2021.pdf",
    "Water Bill Q4 2021.pdf",
    "Water Bill Q1 2022.pdf",
    "Water Bill Q2 2022.pdf",
    "Water Bill Q3 2022.pdf",
    "Water Bill Q4 2022.pdf",
    "Water Bill Q1 2023.pdf",
    "Water Bill Q2 2023.pdf",
    "Water Bill Q3 2023.pdf",
    "Water Bill Q4 2023.pdf",
    "Water Bill Q1 2024.pdf",
    "Water Bill Q2 2024.pdf",
    "Water Bill Q3 2024.pdf",
    "Water Bill Q4 2024.pdf",
    "Water Bill Q1 2025.pdf",
    "Water Bill Q2 2025.pdf",
    "Water Bill Q3 2025.pdf",
    "Water Bill Q4 2025.pdf",
    "Water Bill Q1 2026.pdf",
    "Payment Approvals 2021-2026.xlsx",
    "Water Usage Correspondence.pdf",
    "Water Supplier Contract - Sydney Water.pdf",
    "Plumbing Work Order #WO-2231.pdf",
    "Plumbing Work Order #WO-2401.pdf",
    "Plumbing Work Order #WO-2519.pdf",
    "Plumbing Work Order #WO-2680.pdf",
  ],
};

const SCENARIO_B = {
  paragraphs: [
    "The roof was replaced in March 2022 under a contract with Apex Roofing Solutions, at a total cost of $84,500.",
    "Since that replacement, I have found 6 roof leak events recorded in the scheme's documents, spanning March 2022 to April 2026. These have resulted in 6 work orders and 8 maintenance invoices, with a total rectification cost to the scheme of $14,240.",
    "The most recent prior event before today's foyer leak was recorded in November 2025, involving a leak above the Level 2 stairwell. The associated work order and invoice are available for you to view below.",
    "The foyer area has been the subject of 2 of the 6 recorded leak events. The first was in August 2023, with rectification completed by the same contractor.",
    "Here is a timeline of all recorded leak events and their associated costs since the roof replacement. Is there anything more specific you would like to know?",
  ],
  chart: "/images/ai-chat/poc-leak-timeline.svg",
  chartAlt: "Roof leak timeline Mar 2022 to Apr 2026",
  sources: [
    "Roof Replacement Contract - Apex Roofing.pdf",
    "Roof Replacement Completion Invoice.pdf",
    "Maintenance Invoice #M-2208.pdf",
    "Maintenance Invoice #M-2308.pdf",
    "Maintenance Invoice #M-2403a.pdf",
    "Maintenance Invoice #M-2403b.pdf",
    "Maintenance Invoice #M-2410.pdf",
    "Maintenance Invoice #M-2504a.pdf",
    "Maintenance Invoice #M-2504b.pdf",
    "Maintenance Invoice #M-2511.pdf",
    "Work Order #WO-2208 (Level 3).pdf",
    "Work Order #WO-2308 (Foyer).pdf",
    "Work Order #WO-2403 (L1 storage).pdf",
    "Work Order #WO-2410 (L2 walkway).pdf",
    "Work Order #WO-2504 (Penthouse).pdf",
    "Work Order #WO-2511 (L2 stairwell).pdf",
    "Roof Leak Correspondence.pdf",
  ],
};

function pickScenario(text) {
  const t = (text || "").toLowerCase();
  if (/leak|roof/.test(t)) return SCENARIO_B;
  if (/water|bill/.test(t)) return SCENARIO_A;
  return SCENARIO_A;
}

function thinkingMs() {
  return 1500 + Math.floor(Math.random() * 1000);
}

export default {
  data() {
    return {
      flagEnabled: localStorage.getItem(FLAG_KEY) === "1",
      isOpen: false,
      messages: [],
      inputText: "",
      isThinking: false,
    };
  },

  computed: {
    ...mapGetters({ authenticated: "isAuthenticated" }),
    visible() {
      return this.flagEnabled && this.authenticated;
    },
  },

  methods: {
    open() {
      this.isOpen = true;
      this.$nextTick(() => {
        if (this.$refs.input) this.$refs.input.focus();
      });
    },

    async onSubmit() {
      const text = this.inputText.trim();
      if (!text || this.isThinking) return;

      this.messages.push({ role: "user", text });
      this.inputText = "";
      this.isThinking = true;
      this.messages.push({ role: "thinking" });
      this.scrollToBottom();

      await new Promise((r) => setTimeout(r, thinkingMs()));

      const scenario = pickScenario(text);
      this.messages.pop();
      this.messages.push({
        role: "assistant",
        paragraphs: scenario.paragraphs,
        chart: scenario.chart,
        chartAlt: scenario.chartAlt,
        sources: scenario.sources,
      });

      this.isThinking = false;
      this.scrollToBottom();
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messages;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
.ai-chat-widget {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.ai-chat-bubble {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4296d4 0%, #03458a 100%);
  color: white;
  border: none;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(3, 69, 138, 0.35);
  transition: transform 0.15s, box-shadow 0.15s;
}
.ai-chat-bubble:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgba(3, 69, 138, 0.45);
}
.ai-chat-sparkle {
  width: 26px;
  height: 26px;
}
.ai-chat-sparkle-sm {
  width: 16px;
  height: 16px;
  margin-right: 0.5rem;
  vertical-align: -3px;
}

.ai-chat-panel {
  width: 440px;
  max-width: calc(100vw - 2rem);
  height: calc(100vh - 5rem);
  max-height: 900px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ai-chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: linear-gradient(90deg, #4296d4 0%, #03458a 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}
.ai-chat-header-title {
  display: inline-flex;
  align-items: center;
}
.ai-chat-close {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
  padding: 0 0.25rem;
}

.ai-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: #f9fafb;
}

.ai-chat-empty {
  text-align: center;
  color: #6b7280;
  font-size: 13px;
  padding: 2rem 1rem;
}
.ai-chat-empty-hint {
  margin-top: 0.5rem;
  font-size: 12px;
}
.ai-chat-empty em {
  background: #e5e7eb;
  padding: 1px 6px;
  border-radius: 3px;
  font-style: normal;
  font-family: -apple-system, monospace;
  font-size: 11px;
}

.ai-chat-msg {
  margin-bottom: 0.75rem;
  display: flex;
}
.ai-chat-msg-user {
  justify-content: flex-end;
}
.ai-chat-msg-assistant,
.ai-chat-msg-thinking {
  justify-content: flex-start;
}

.ai-chat-user-bubble {
  background: #4296d4;
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 16px 16px 4px 16px;
  max-width: 80%;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
}

.ai-chat-assistant-bubble {
  background: white;
  border: 1px solid #e5e7eb;
  padding: 0.75rem 1rem;
  border-radius: 16px 16px 16px 4px;
  max-width: 92%;
  font-size: 13px;
  line-height: 1.5;
  color: #1f2937;
}

.ai-chat-text p {
  margin: 0 0 0.5rem 0;
}
.ai-chat-text p:last-child {
  margin-bottom: 0;
}

.ai-chat-chart {
  display: block;
  width: 100%;
  margin-top: 0.5rem;
  border-radius: 4px;
  background: white;
}

.ai-chat-sources {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
}
.ai-chat-sources-label {
  font-size: 10px;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.06em;
  margin-bottom: 0.5rem;
  font-weight: 600;
}
.ai-chat-source-tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}
.ai-chat-source-tile {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.5rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 11px;
  color: #374151;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ai-chat-source-tile i {
  color: #6b7280;
  font-size: 11px;
}

.ai-chat-thinking {
  display: inline-flex;
  gap: 5px;
  align-items: center;
  padding: 0.75rem 1rem;
}
.ai-chat-dot {
  width: 7px;
  height: 7px;
  background: #9ca3af;
  border-radius: 50%;
  display: inline-block;
  animation: ai-chat-bounce 1.4s infinite ease-in-out both;
}
.ai-chat-dot:nth-child(1) {
  animation-delay: -0.32s;
}
.ai-chat-dot:nth-child(2) {
  animation-delay: -0.16s;
}
@keyframes ai-chat-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.4);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.ai-chat-input {
  display: flex;
  padding: 0.75rem;
  background: white;
  border-top: 1px solid #e5e7eb;
  gap: 0.5rem;
}
.ai-chat-input input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
}
.ai-chat-input input:focus {
  border-color: #4296d4;
  box-shadow: 0 0 0 3px rgba(66, 150, 212, 0.18);
}
.ai-chat-input button {
  padding: 0.5rem 0.85rem;
  background: #4296d4;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}
.ai-chat-input button:hover:not(:disabled) {
  background: #2779bd;
}
.ai-chat-input button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}
</style>
